import { createContext, useContext, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";

const BrandCtx = createContext(null);

/**
 * Reads `user.brand` from the auth session and writes it to CSS variables
 * on `document.documentElement`. Downstream components can either use the
 * CSS vars (`--brand-primary`) directly or `useBrand()` to read the raw
 * values. Falls back to the FORM C defaults when no user is signed in.
 */
const DEFAULTS = {
  firm_name: "FORM C",
  tagline: "Corporate services · Cyprus",
  primary_color: "#0A1F44",
  accent_color: "#C99A3E",
  logo_url: null,
  favicon_url: null,
  support_email: null,
};

export function BrandProvider({ children }) {
  const { user } = useAuth();
  const brand = useMemo(() => ({ ...DEFAULTS, ...(user?.brand || {}) }), [user]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", brand.primary_color);
    root.style.setProperty("--brand-accent", brand.accent_color);
    // Also update <title> and favicon so browser chrome reflects the tenant.
    if (brand.firm_name) document.title = brand.firm_name;
    if (brand.favicon_url) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = brand.favicon_url;
    }
  }, [brand]);

  return <BrandCtx.Provider value={brand}>{children}</BrandCtx.Provider>;
}

export function useBrand() {
  return useContext(BrandCtx) || DEFAULTS;
}
